import { useState } from "react";
import { ApiFactory } from "../../core/config/ApiFactory";
import { useSnackbar } from "../../core/contexts/SnackbarContext"
import { ICategoria } from "../interfaces/ICategoria"
import { IProduto } from "../interfaces/IProduto";
import { v4 } from "uuid";
import { IImagemProduto } from "../interfaces/IImagemProduto";
import { IReabastecerControleEstoqueProduto } from "../interfaces/IReabastecerControleEstoqueProduto";
import { IConsumirControleEstoqueProduto } from "../interfaces/IConsumirControleEstoqueProduto";
import { IProdutoPedido } from "../../pedidos/interface/IProdutoPedido";
import { TipoProduto } from "../enum/TipoProduto";
import { IItemProduto } from "../../pedidos/interface/IItemProduto";
import { IComplementoItensPedido } from "../../pedidos/interface/IComplementoItensPedido";
import { TipoItemProduto } from "../enum/TipoItemProduto";
import { tratarItensComplemento } from "../../pedidos/utils/calcularPrecoComplemento";
import { INcm } from "../interfaces/INcm";
import { ICfop } from "../interfaces/ICfop";

export interface ItemAtualizarImagem {
    uuid: string;
    tipo: 'Produto' | 'Item' | 'Sabor' | 'Borda' | 'Massa';
    descricao: string;
    imagens?: IImagemProduto[];
    categoriaUuid?: string;
    produtoUuid?: string;
    complementoUuid?: string;
    itemUuid?: string;
}

export const useProdutos = () => {
    const [loading, setLoading] = useState(false);
    const snackbar = useSnackbar();
    const apiProdutos = ApiFactory.getApi("MS_PRODUTOS");

    const atualizarImagensEmMassa = async (imagem: File, itens: ItemAtualizarImagem[]) => {
        setLoading(true);
        const formData = new FormData();
        formData.append('imagem', imagem);
        formData.append('itens', JSON.stringify(itens));
        try {
            const { data } = await apiProdutos.patch(`/imagem`, formData);
            return data;
        } catch (error) {
            return null;
        } finally {
            setLoading(false);
        }
    }

    const uploadImagem = async (imagem: File, produtoUuid: string) => {
        setLoading(true);

        const formData = new FormData();

        formData.append('imagem', imagem);

        try {
            const { data } = await apiProdutos.post(`/${produtoUuid}/imagem`, formData);
            return data;
        } catch (error) {
            return null;
        } finally {
            setLoading(false);
        }
    }

    const listarCategorias = async (): Promise<ICategoria[] | null> => {
        setLoading(true);

        try {
            const { data } = await apiProdutos.get('/categorias');
            return data;
        } catch (error) {
            snackbar("Ocorreu um erro ao listar as categorias", { severity: "error" })
            return null;
        } finally {
            setLoading(false);
        }
    }

    const copiarProduto = async (categorias: ICategoria[], categoriaUuidDestino: string, produtoUuid: string) => {
        if (!categoriaUuidDestino) {
            snackbar("Categoria de destino inválida", { severity: "error" });
            return false;
        }

        if (!produtoUuid) {
            snackbar("Produto inválido", { severity: "error" });
            return false;
        }

        if (!categorias?.length) {
            snackbar("Nenhuma categoria encontrada", { severity: "error" });
            return false;
        }

        let produtoEncontrado: IProduto = undefined as unknown as IProduto;

        categorias.forEach(categoria => {
            const produtoProcurado = categoria.produtos?.find(produto => produto.uuid === produtoUuid);
            if (produtoProcurado) {
                produtoEncontrado = produtoProcurado;
            }
        });

        if (!produtoEncontrado) {
            snackbar("Produto não encontrado", { severity: "error" });
            return false;
        }

        const produtoParaSalvar = {
            ...produtoEncontrado,
            uuid: v4(),
            descricao: `${produtoEncontrado.descricao} - Cópia`
        };

        await salvar(categoriaUuidDestino, produtoParaSalvar);
    };

    const salvar = async (categoriaUuid: string, produto: IProduto) => {
        if (!categoriaUuid) {
            snackbar("Categoria inválida", { severity: "error" })
            return false;
        }

        if (!produto) {
            snackbar("Produto inválido", { severity: "error" })
            return false;
        }

        if (!produto.uuid) {
            produto.uuid = v4();
        }

        setLoading(true)

        const imagens: IImagemProduto[] = [];

        if (produto.imagens?.length) {
            await Promise.all(produto.imagens.map(async imagem => {
                if (imagem instanceof File) {
                    const arquivo = await uploadImagem(imagem, produto.uuid);
                    if (arquivo) {
                        imagens.push(arquivo);
                    }
                } else {
                    imagens.push(imagem);
                }
            }))

            produto.imagens = imagens;
        }

        if (produto.complementos && produto.complementos.length) {
            await Promise.all(produto?.complementos?.map(async complemento => {
                if (complemento.itens && complemento.itens.length) {
                    await Promise.all(
                        complemento?.itens?.map(async item => {
                            const imagensItem: IImagemProduto[] = [];
                            if (item.imagens && item.imagens.length) {
                                await Promise.all(item.imagens?.map(async imagem => {
                                    if (imagem instanceof File) {
                                        const arquivo = await uploadImagem(imagem, item.uuid);
                                        if (arquivo) {
                                            imagensItem.push(arquivo);
                                        }
                                    } else {
                                        imagensItem.push(imagem);
                                    }
                                }))
                            }
                            item.imagens = imagensItem;
                        })
                    )
                }
            }))
        }

        if (produto.massas && produto.massas.length) {
            await Promise.all(
                produto?.massas?.map(async massa => {
                    const imagensItem: IImagemProduto[] = [];
                    if (massa.imagens && massa.imagens.length) {
                        await Promise.all(massa.imagens?.map(async imagem => {
                            if (imagem instanceof File) {
                                const arquivo = await uploadImagem(imagem, massa.uuid ?? v4());
                                if (arquivo) {
                                    imagensItem.push(arquivo);
                                }
                            } else {
                                imagensItem.push(imagem);
                            }
                        }))
                    }
                    massa.imagens = imagensItem;
                })
            )
        }

        if (produto.bordas && produto.bordas.length) {
            await Promise.all(
                produto?.bordas?.map(async borda => {
                    const imagensItem: IImagemProduto[] = [];
                    if (borda.imagens && borda.imagens.length) {
                        await Promise.all(borda.imagens?.map(async imagem => {
                            if (imagem instanceof File) {
                                const arquivo = await uploadImagem(imagem, borda.uuid ?? v4());
                                if (arquivo) {
                                    imagensItem.push(arquivo);
                                }
                            } else {
                                imagensItem.push(imagem);
                            }
                        }))
                    }
                    borda.imagens = imagensItem;
                })
            )
        }

        if (produto.sabores && produto.sabores.length) {
            await Promise.all(
                produto?.sabores?.map(async sabor => {
                    const imagensItem: IImagemProduto[] = [];
                    if (sabor.imagens && sabor.imagens.length) {
                        await Promise.all(sabor.imagens?.map(async imagem => {
                            if (imagem instanceof File) {
                                const arquivo = await uploadImagem(imagem, sabor.uuid ?? v4());
                                if (arquivo) {
                                    imagensItem.push(arquivo);
                                }
                            } else {
                                imagensItem.push(imagem);
                            }
                        }))
                    }
                    sabor.imagens = imagensItem;
                })
            )
        }

        if (produto.adicionais && produto.adicionais.length) {
            await Promise.all(
                produto.adicionais.map(async adicional => {
                    const imagensItem: IImagemProduto[] = [];
                    if (adicional.imagens && adicional.imagens.length) {
                        await Promise.all(adicional.imagens.map(async imagem => {
                            if (imagem instanceof File) {
                                const arquivo = await uploadImagem(imagem, adicional.uuid ?? v4());
                                if (arquivo) {
                                    imagensItem.push(arquivo);
                                }
                            } else {
                                imagensItem.push(imagem);
                            }
                        }))
                    }
                    adicional.imagens = imagensItem;
                })
            )
        }

        try {
            const { data } = await apiProdutos.post(`/categorias/${categoriaUuid}`, produto);

            if (data) {
                return true;
            }

            return false;
        } catch (error) {
            return false;
        } finally {
            setLoading(false);
        }
    }

    const tratarProduto = (produto: IProdutoPedido): IProdutoPedido | undefined => {
        if (produto.tipo == TipoProduto.pizza) {
            const itens: IItemProduto[] = [];
            let complementosItens: IComplementoItensPedido[] = [];

            if (produto.borda) {
                itens.push({
                    descricao: produto.borda.descricao,
                    preco: produto.borda.preco,
                    quantidade: 1,
                    tipo: TipoItemProduto.borda,
                    uuidReferencia: produto.borda.uuid
                })

                complementosItens.push({
                    descricao: 'Borda',
                    itens: [produto.borda.uuid],
                    uuid: v4(),
                    numeroMaximoItens: 1,
                    numeroMinimoItens: 1
                })
            }

            if (produto.massa) {
                itens.push({
                    descricao: produto.massa.descricao,
                    preco: produto.massa.preco,
                    quantidade: 1,
                    tipo: TipoItemProduto.massa,
                    uuidReferencia: produto.massa.uuid
                })

                complementosItens.push({
                    descricao: 'Massa',
                    itens: [produto.massa.uuid],
                    uuid: v4(),
                    numeroMaximoItens: 1,
                    numeroMinimoItens: 1
                })
            }

            if (produto.configuracao?.modeloPrecificacao === "maiorPreco") {
                precificarPorMaiorPreco(produto, itens, complementosItens);
            } else if (produto.configuracao?.modeloPrecificacao === "precoDividido") {
                precificarPorPrecoDividido(produto, itens, complementosItens);
            } else if (produto.configuracao?.modeloPrecificacao === "precoMisto") {
                precificarPorPrecoMisto(produto, itens, complementosItens);
            }

            if (produto.adicionaisSelecionados?.length) {
                produto.adicionaisSelecionados.forEach(adicional => {
                    itens.push({
                        descricao: `Adc: ${adicional.descricao}`,
                        preco: adicional.preco,
                        quantidade: 1,
                        tipo: TipoItemProduto.item,
                        uuidReferencia: adicional.uuid
                    });
                });
            }

            if (produto.observacao) {
                itens.push({
                    descricao: produto.observacao,
                    preco: produto.valorAdicional ?? 0,
                    quantidade: 1,
                    tipo: TipoItemProduto.observacao
                })
            }

            return {
                ...produto,
                itens,
                quantidade: 1,
                uuid: v4(),
                tipoProdutoReferencia: produto.tipo,
                complementosItens,
                observacao: produto.observacao,
                complementos: produto.complementos
            }
        }

        if (produto.tipo == TipoProduto.preparado) {
            const itens: IItemProduto[] = [];
            const complementosItens: IComplementoItensPedido[] = [];

            produto.complementos?.map(complemento => {
                if (complemento?.itensSelecionados?.length) {
                    complementosItens.push({
                        descricao: complemento.descricao,
                        uuid: complemento.uuid,
                        itens: complemento.itensSelecionados?.flatMap(item => Array(item.quantidade ?? 1).fill(item.uuid)),
                        numeroMaximoItens: complemento.numeroMaximoItens,
                        numeroMinimoItens: complemento.numeroMinimoItens
                    })

                    itens.push(...tratarItensComplemento(complemento));
                }
            })

            if (produto.observacao) {
                itens.push({
                    descricao: produto.observacao,
                    preco: produto.valorAdicional ?? 0,
                    quantidade: 1,
                    tipo: TipoItemProduto.observacao
                })
            }

            return {
                ...produto,
                quantidade: 1,
                uuid: v4(),
                itens,
                tipoProdutoReferencia: produto.tipo,
                complementosItens,
                observacao: produto.observacao,
                complementos: produto.complementos
            }
        }

        if (produto.tipo == TipoProduto.industrializado) {
            return {
                ...produto,
                quantidade: 1,
                uuid: v4(),
                tipoProdutoReferencia: produto.tipo
            }
        }

        if (produto.tipo == TipoProduto.peso) {
            return {
                ...produto,
                quantidade: 1,
                uuid: v4(),
                tipoProdutoReferencia: produto.tipo
            }
        }
    }


    function precificarPorMaiorPreco(produto: IProduto, itens: IItemProduto[], complementosItens: IComplementoItensPedido[]) {
        let maiorPreco = 0;

        // Encontrar o maior preço entre os sabores
        produto.sabores?.forEach((sabor) => {
            if (sabor.preco > maiorPreco) {
                maiorPreco = sabor.preco;
            }
        });

        let encontrouMaiorPreco = false;

        const saboresAtualizados = produto.sabores?.map((sabor) => {
            if (sabor.preco === maiorPreco) {
                if (!encontrouMaiorPreco) {
                    encontrouMaiorPreco = true; // Mantém o primeiro encontrado com maior preço
                    return { ...sabor };
                }
                return { ...sabor, preco: 0 }; // Zera os outros sabores com o mesmo preço máximo
            }
            return { ...sabor, preco: 0 };
        });

        const complemento: IComplementoItensPedido = {
            descricao: 'Sabores',
            itens: [],
            uuid: v4(),
            numeroMaximoItens: produto.quantidadeSabores,
            numeroMinimoItens: 1
        };

        saboresAtualizados?.forEach(sabor => {
            itens.push({
                descricao: sabor.descricao,
                preco: sabor.preco,
                quantidade: 1,
                tipo: TipoItemProduto.sabor,
                uuidReferencia: sabor.uuid
            });
            complemento.itens.push(sabor.uuid);
        });

        complementosItens.push(complemento);
    }


    function precificarPorPrecoDividido(produto: IProduto, itens: IItemProduto[], complementosItens: IComplementoItensPedido[]) {
        const qtdSabores = produto.sabores?.length ?? 0;
        const somaPrecos = produto.sabores?.reduce((total, sabor) => total + sabor.preco, 0);
        const valorMedio = Math.ceil((somaPrecos ?? 0) / qtdSabores);
        const saboresAtualizados = produto.sabores?.map((sabor) => ({ ...sabor, preco: valorMedio / (produto.sabores?.length ?? 1) }));

        const complemento: IComplementoItensPedido = {
            descricao: 'Sabores',
            itens: [],
            uuid: v4(),
            numeroMaximoItens: produto.quantidadeSabores,
            numeroMinimoItens: 1
        };

        saboresAtualizados?.forEach(sabor => {
            itens.push({
                descricao: sabor.descricao,
                preco: sabor.preco,
                quantidade: 1,
                tipo: TipoItemProduto.sabor,
                uuidReferencia: sabor.uuid
            });

            complemento.itens.push(sabor.uuid);
        });

        complementosItens.push(complemento);
    }

    function precificarPorPrecoMisto(produto: IProduto, itens: IItemProduto[], complementosItens: IComplementoItensPedido[]) {
        if ((produto.sabores?.length ?? 0) > 2) {
            precificarPorMaiorPreco(produto, itens, complementosItens);
        } else {
            precificarPorPrecoDividido(produto, itens, complementosItens);
        }
    }


    const excluir = async (categoriaUuid: string, produtoUuid: string) => {
        if (!categoriaUuid) {
            snackbar("Categoria inválida", { severity: "error" })
            return false;
        }

        if (!produtoUuid) {
            snackbar("Produto inválido", { severity: "error" })
            return false;
        }

        setLoading(true);

        try {
            const { data } = await apiProdutos.delete(`/categorias/${categoriaUuid}/${produtoUuid}`);

            if (data) {
                return true;
            }

            return false;
        } catch (error) {
            return false;
        } finally {
            setLoading(false);
        }
    }

    const consultarIndustrializado = async (codigoBarras: string) => {
        setLoading(true);

        try {
            const { data } = await apiProdutos.get(`/industrializados/${codigoBarras}`);
            return data;
        } catch (error) {
            return null;
        } finally {
            setLoading(false);
        }
    }

    const buscarNcms = async (filtro: { descricao?: string; codigo?: string }): Promise<INcm[]> => {
        const params: { descricao?: string; codigo?: string } = {};
        if (filtro.descricao?.trim()) params.descricao = filtro.descricao.trim();
        if (filtro.codigo?.trim()) params.codigo = filtro.codigo.trim();

        if (!params.descricao && !params.codigo) {
            return [];
        }

        try {
            const { data } = await apiProdutos.get(`/ncms`, { params });
            return Array.isArray(data) ? data : [];
        } catch (error) {
            return [];
        }
    }

    const buscarCfops = async (filtro: { descricao?: string; codigo?: string }): Promise<ICfop[]> => {
        const params: { descricao?: string; codigo?: string } = {};
        if (filtro.descricao?.trim()) params.descricao = filtro.descricao.trim();
        if (filtro.codigo?.trim()) params.codigo = filtro.codigo.trim();

        if (!params.descricao && !params.codigo) {
            return [];
        }

        try {
            const { data } = await apiProdutos.get(`/cfops`, { params });
            return Array.isArray(data) ? data : [];
        } catch (error) {
            return [];
        }
    }

    const reabastecer = async (produtos: IReabastecerControleEstoqueProduto[]) => {
        if (!produtos.length) {
            snackbar("Nenhum produto recebido para o reabastecimento", { severity: "error" });
            return null;
        }
        setLoading(true);
        try {
            const { data } = await apiProdutos.put("/estoque/reabastecer/produtos", produtos);

            if (data) {
                snackbar("Produto(s) reabastecido(s) com sucesso", { severity: "success" });
                return data;
            }

            snackbar("Ocorreu um erro ao reabastecer o(s) produto(s)", { severity: "error" })
            return null;
        } catch (error) {
            snackbar("Ocorreu um erro ao reabastecer o(s) produto(s)", { severity: "error" })
            return null;
        } finally {
            setLoading(false);
        }
    }

    const consumir = async (produtos: IConsumirControleEstoqueProduto[]) => {
        if (!produtos.length) {
            snackbar("Nenhum produto recebido para o consumo", { severity: "error" });
            return null;
        }
        setLoading(true);
        try {
            const { data } = await apiProdutos.put("/estoque/consumir/produtos", produtos);

            if (data) {
                snackbar("Produto(s) consumido(s) com sucesso", { severity: "success" });
                return data;
            }

            snackbar("Ocorreu um erro ao consumir o(s) produto(s)", { severity: "error" })
            return null;
        } catch (error) {
            snackbar("Ocorreu um erro ao consumir o(s) produto(s)", { severity: "error" })
            return null;
        } finally {
            setLoading(false);
        }
    }

    return { salvar, excluir, loading, consultarIndustrializado, buscarNcms, buscarCfops, consumir, reabastecer, listarCategorias, copiarProduto, uploadImagem, atualizarImagensEmMassa, tratarProduto }
}